<script setup lang="ts">
import { computed, ref } from 'vue';
import { ModalDialog, Address, formatHelpers } from '@hfc/ui-core';
import { AddressSuggestion } from '@/application/shared/domain/AddressValidationResponse';
import AddressValidationElement from '@/application/shared/infrastructure/components/AddressValidationElement.vue';
import { ADDRESS_TYPE } from '@hfc/common/src/types/AddressTypes';

const { capitalizeFirstLetter } = formatHelpers();

const props = withDefaults(defineProps<{
  suggestions: Array<AddressSuggestion>;
  originalAddress?: AddressSuggestion | Address;
  addressType?: ADDRESS_TYPE;
  strictAddressValidation?: boolean;
}>(), {
  strictAddressValidation: false,
  addressType: ADDRESS_TYPE.HOME
});

interface EmitsInterface {
  (event: 'close'): void;
  (event: 'confirm', address?: AddressSuggestion): void;
}
const emits = defineEmits<EmitsInterface>();

const selectedSuggestion = ref<number | null>(null);
const hasSuggestions = computed(() => props.suggestions && props.suggestions.length);

const selectAddress = (value: number | null): void => {
  selectedSuggestion.value = value;
};

const confirmAddress = (): void => {
  if (selectedSuggestion.value !== null) {
    const suggestion = props.suggestions[selectedSuggestion.value];

    return emits('confirm', suggestion);
  }
  emits('confirm');
};

const showConfirmButton = computed(() => {
  return !props.strictAddressValidation || (props.strictAddressValidation && selectedSuggestion.value !== null);
});

const modalTitle = computed(() => {
  if (!props.strictAddressValidation || hasSuggestions.value) {
    return `Confirm your ${props.addressType} address`;
  }

  return capitalizeFirstLetter(`${props.addressType} address not found`);
});

</script>
<template>
  <ModalDialog @close="emits('close')" :title="modalTitle" @confirm="confirmAddress" :show-confirm-button="showConfirmButton">
    <template #content>
      <div v-if="hasSuggestions">
        <p>The address you entered could not be found. However, we've identified an address that closely matches the one you entered. Please select the correct address from the options below, and click "Ok" to continue. If you'd like to change your address, click "Cancel" to close this window.</p><br>
        <div class="flex flex-wrap flex-row">
          <AddressValidationElement v-for="(suggestion, i) in suggestions" :key="i" :line-one="suggestion.line_one" :line-two="suggestion.line_two" :city="suggestion.city" :state="suggestion.state" :zip="`${suggestion.zip_code}`" :id="i" :title="`Suggested ${props.addressType} Address ${i + 1}`" :active="selectedSuggestion === i" @selectAddress="selectAddress" class="light" title-class="capitalize" />
          <AddressValidationElement v-if="originalAddress" :line-one="originalAddress.line_one" :line-two="originalAddress.line_two" :city="originalAddress.city" :state="originalAddress.state" :zip="`${originalAddress.zip_code}`" :id="null" :title="`Original Address`" :active="selectedSuggestion === null" @selectAddress="selectAddress" class="light"/>
        </div>
      </div>
      <div v-else>
        <p class="mb-4">The address you entered could not be found, and no alternatives can be suggested. Please enter a valid address (click "Cancel" to close this window).</p>
        <p v-if="!strictAddressValidation">If you made a mistake, you can fix it. If the address you entered is correct, then click "Ok" to continue</p><br>
      </div>
    </template>
  </ModalDialog>
</template>
